<template>
  <div class="row timeline col-lg-10">
    <div class="container-fluid timeline-sticky">
      <div id="timelineComponent">
        <!-- DESKTOP DISPLAY -->
        <div class="row align-items-center p-2" v-if="!isSmartphoneDevice && !isSmallScreen">
          <!-- TIMELINE VIEW -->
          <svg-timeline
            :chart-id="chartId"
            :start="start"
            :end="end"
            @update="updateDateRangeBrush"
            @validate="onValidDateRangeBrush"
          />
          <!-- PRESET DROPDOWNS -->
          <div :class="selectedPresetClass">
            <small>{{ $t('timeline.preset') }}</small
            ><br />
            <button
              class="btn btn-chart btn dropdown-toggle"
              type="button"
              id="dropdownPresetButton"
              data-toggle="dropdown"
              aria-haspopup="true"
              aria-expanded="false"
              v-tooltip="{ content: $t('timeline.select_preset'), placement: 'top-center' }"
            >
              <i class="fas fa-calendar-alt"></i>
            </button>
            <div class="dropdown-menu dropdown-menu-left" aria-labelledby="dropdownPresetButton">
              <a
                class="dropdown-item"
                value="last_weekend"
                @click="onDatePresetClick('last_weekend')"
                >{{ $t('timeline.last_weekend') }}</a
              >
              <a class="dropdown-item" value="last_week" @click="onDatePresetClick('last_week')">{{
                $t('timeline.last_week')
              }}</a>
              <a
                class="dropdown-item"
                value="last_month"
                @click="onDatePresetClick('last_month')"
                >{{ $t('timeline.last_month') }}</a
              >
              <a class="dropdown-item" value="last_year" @click="onDatePresetClick('last_year')">{{
                $t('timeline.last_year')
              }}</a>
              <a
                class="dropdown-item"
                value="since_first_january"
                @click="onDatePresetClick('since_first_january')"
                >{{ $t('timeline.since_first_january') }}</a
              >
              <a
                class="dropdown-item"
                :value="'year-' + year"
                v-for="year in presetYears"
                :key="year"
                @click="onDatePresetClick('year-' + year)"
                >{{ $t('timeline.year', [year]) }}</a
              >
              <a
                class="dropdown-item"
                value="since_launch"
                v-if="isOnlyOneSite"
                @click="onDatePresetClick('since_launch')"
                >{{ $t('timeline.launch') }}</a
              >
            </div>
          </div>
          <div :class="datePickerClass">
            <div class="form-row" v-if="timelineDatePickerStart && timelineDatePickerEnd">
              <div class="col-md-6 mb-3">
                <small for="inputStartDate">{{ $t('timeline.from') }}</small>
                <date-picker
                  id="input-start-date"
                  :value="timelineDatePickerStart"
                  :config="config"
                  @dp-hide="onStartDateChange"
                  v-tooltip="{ content: timelineDatePickerStart, placement: 'bottom-center' }"
                ></date-picker>
              </div>
              <div class="col-md-6 mb-3">
                <small for="inputEndDate">{{ $t('timeline.to') }}</small>
                <date-picker
                  id="input-end-date"
                  :value="timelineDatePickerEnd"
                  :config="config"
                  @dp-hide="onEndDateChange"
                  v-tooltip="{ content: timelineDatePickerEnd, placement: 'bottom-center' }"
                ></date-picker>
              </div>
            </div>
          </div>
        </div>
        <!-- Smartphone display -->
        <div class="row align-items-center p-2" v-if="isSmallScreen || isSmartphoneDevice">
          <span
            class="dropdown-toggle"
            data-toggle="collapse"
            href="#datePickerCollapse"
            role="button"
            aria-expanded="false"
            aria-controls="datePickerCollapse"
          >
            <span
              ><i class="fas fa-calendar-alt mr-2"></i>{{ $t('timeline.from') }}
              <strong>{{ timelineDatePickerStart }}</strong>
              {{ String($t('timeline.to')).toLowerCase() }}
              <strong>{{ timelineDatePickerEnd }}</strong></span
            >
          </span>
          <div :class="collapseClass" id="datePickerCollapse">
            <div class="form-group mb-0">
              <small for="inputDatePreset">{{ $t('timeline.preset') }}</small>
              <select
                id="inputDatePreset"
                class="form-control"
                v-model="selectedPreset"
                @change="onDatePresetSelect"
              >
                <option value="custom" selected>{{ $t('timeline.custom') }}</option>
                <option value="last_weekend">{{ $t('timeline.last_weekend') }}</option>
                <option value="last_week">{{ $t('timeline.last_week') }}</option>
                <option value="last_month">{{ $t('timeline.last_month') }}</option>
                <option value="last_year">{{ $t('timeline.last_year') }}</option>
                <option value="since_first_january">
                  {{ $t('timeline.since_first_january') }}
                </option>
                <option :value="'year-' + year" v-for="year in presetYears" :key="year">
                  {{ $t('timeline.year', [year]) }}
                </option>
                <option value="since_launch" v-if="isOnlyOneSite">
                  {{ $t('timeline.launch') }}
                </option>
              </select>
            </div>
            <div :class="datePickerClass">
              <div class="form-row" v-if="timelineDatePickerStart && timelineDatePickerEnd">
                <div class="col-md-6">
                  <small for="inputStartDate">{{ $t('timeline.from') }}</small>
                  <date-picker
                    id="input-start-date"
                    :value="timelineDatePickerStart"
                    :config="config"
                    @dp-hide="onStartDatePickerChange"
                  ></date-picker>
                </div>
                <div class="col-md-6 mb-3">
                  <small for="inputEndDate">{{ $t('timeline.to') }}</small>
                  <date-picker
                    id="input-end-date"
                    :value="timelineDatePickerEnd"
                    :config="config"
                    @dp-hide="onEndDatePickerChange"
                  ></date-picker>
                </div>
              </div>
            </div>
            <div class="form-inline">
              <button
                class="btn btn-dark col-sm-12"
                @click="validatePeriod"
                data-toggle="collapse"
                href="#datePickerCollapse"
                aria-controls="datePickerCollapse"
                aria-expanded="false"
              >
                {{ $t('validate') }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment'
import datePicker from 'vue-bootstrap-datetimepicker'
import { utilsMixin, timeMixin } from '../utils/mixins'

export default {
  name: 'Timeline',
  dependencies: ['adminService'],
  mixins: [utilsMixin, timeMixin],
  data() {
    return {
      moment: moment,
      datePicker: datePicker,
      config: {
        format: 'DD/MM/YYYY',
        useCurrent: false,
        locale: this.$locale,
      },
      savedStart: null,
      savedEnd: null,
      selectedPreset: 'last_month',
      isJustMounted: true,
      sitesLaunch: {},
      selectedPeriod: {},
      timelineDatePickerStart: null,
      timelineDatePickerEnd: null,
    }
  },
  components: {
    datePicker,
    'svg-timeline': () => import('./utils/SVGTimeline.vue'),
  },
  props: ['chartId', 'start', 'end', 'isOnlyOneSite', 'siteIDs'],
  mounted() {
    this.initPreset()
    window.addEventListener('resize', this.handleTimelineResize)
    this.timelineDatePickerStart = moment(this.start).format(this.dateFormat)
    this.timelineDatePickerEnd = moment(this.end).format(this.dateFormat)
  },
  computed: {
    selectedPresetClass: function() {
      return {
        'col-sm-1': this.isLargeScreen || this.isVeryLargeScreen,
        'col-sm-2': this.isMediumScreen,
        'col-sm-6': this.isSmallScreen,
        'my-auto': true,
        'pr-2': true,
        'pl-2': true,
        'pb-3': !this.isSmartphoneDevice,
        dropdown: true,
      }
    },
    datePickerClass: function() {
      return {
        'col-sm-2': !this.isSmallScreen && !this.isMediumScreen,
        'col-sm-12': this.isMediumScreen && this.isSmartphoneDevice,
        'col-sm-8': this.isMediumScreen && !this.isSmartphoneDevice,
        'date-picker': !this.isMobileScreen ? true : false,
        'my-auto': true,
        'pr-1': true,
        'pl-1': true,
      }
    },
    datePickerLabelClass: function() {
      return {
        'control-label': true,
        'mb-3': true,
        'pl-0': true,
        'pr-0': true,
        'col-sm-2': true,
        'sr-only': this.isSmartphoneDevice,
      }
    },
    collapseClass: function() {
      //collapse col-sm-12 pt-3
      return {
        'mt-2': true,
        'mb-2': true,
        collapse: true,
        'col-sm-10': this.isMediumScreen,
        'col-sm-12': this.isSmallScreen,
      }
    },
    presetYears: function() {
      var presentYear = parseInt(moment().format('YYYY'))
      var presetYears = []
      for (var i = 2016; i <= presentYear; i++) {
        presetYears.push(i)
      }
      return presetYears
    },
  },
  watch: {
    siteIDs: {
      handler: function() {
        if (this.selectedPreset == 'since_launch') {
          this.selectedPreset = 'custom'
        }
      },
      immediate: true,
    },
    isOnlyOneSite: {
      handler: function() {},
      immediate: true,
    },
    start: {
      handler: function() {
        this.timelineDatePickerStart = this.start
      },
    },
    end: {
      handler: function() {
        this.timelineDatePickerEnd = this.end
      },
    },
  },
  methods: {
    handleTimelineResize(event) {
      this.handleResize(event)
      if (this.isLargeScreen || this.isVeryLargeScreen) {
        this.initPreset()
      }
    },
    onStartDateChange: function(e) {
      // eslint-disable-next-line
      let start = moment($(e.target).val(), this.dateFormat)
      let end = moment(this.end, this.dateFormat)

      if (start.isAfter(end)) {
        start = end.subtract(1, 'day')
      }

      // Emit new date range
      this.$emit('update', {
        start: start,
        end: end,
      })

      setTimeout(() => {
        this.timelineDatePickerStart = moment(start, this.dateFormat).format(this.dateFormat)
      }, 300)

      this.$log.debug('onStartDateChange', start, end)
    },
    onEndDateChange: function(e) {
      // eslint-disable-next-line
      let end = moment($(e.target).val(), this.dateFormat)
      let start = moment(this.start, this.dateFormat)

      if (end.isBefore(start)) {
        end = start.add(1, 'day')
      }

      // Emit new date range
      this.$emit('update', {
        start: start,
        end: end,
      })

      setTimeout(() => {
        this.timelineDatePickerStart = moment(start, this.dateFormat).format(this.dateFormat)
      }, 300)

      this.$log.debug('onStartDateChange', start, end)
    },
    onStartDatePickerChange: function(e) {
      if (!this.selectedPeriod) {
        this.selectedPeriod = {
          start: new Date(),
          end: new Date(),
        }
      }
      // eslint-disable-next-line
      this.selectedPeriod.start = moment($(e.target).val(), this.dateFormat)
      this.selectedPeriod.end = moment(this.timelineDatePickerEnd, this.dateFormat)
    },
    onEndDatePickerChange: function(e) {
      if (!this.selectedPeriod) {
        this.selectedPeriod = {
          start: new Date(),
          end: new Date(),
        }
      }

      this.selectedPeriod.start = moment(this.timelineDatePickerStart, this.dateFormat)
      // eslint-disable-next-line
      this.selectedPeriod.end = moment($(e.target).val(), this.dateFormat)
      this.$log.debug('onEndDatePickerChange', this.selectedPeriod.start, this.selectedPeriod.end)
    },
    onDatePresetClick: function(preset) {
      this.selectedPreset = preset
      this.onDatePresetChange()
    },
    onDatePresetSelect: function() {
      this.onDatePresetChange()
    },
    onDatePresetChange: function() {
      let end = moment(new Date(), this.dateFormat)
      let start = new Date()

      Promise.resolve()
        .then(() => {
          if (this.selectedPreset == 'last_weekend') {
            start = moment()
              .startOf('isoWeek')
              .subtract(2, 'days')
            end = moment()
              .startOf('isoWeek')
              .subtract(1, 'days')
          } else if (this.selectedPreset == 'last_week') {
            start = moment(end, this.dateFormat).subtract(7, 'days')
          } else if (this.selectedPreset == 'last_month') {
            start = moment(end, this.dateFormat).subtract(30, 'days')
          } else if (this.selectedPreset == 'last_year') {
            start = moment(end, this.dateFormat).subtract(1, 'year')
          } else if (this.selectedPreset == 'since_first_january') {
            start = moment('01/01/' + moment().year(), this.dateFormat)
          } else if (this.selectedPreset == 'since_launch') {
            // eslint-disable-next-line
            if (this.sitesLaunch.hasOwnProperty(this.siteIDs[0])) {
              start = moment(this.sitesLaunch[this.siteIDs[0]], 'YYYY-MM-DD')
            } else {
              if (this.getCurrentUser.role == 'partner' && this.getCurrentUser.sites.length == 1) {
                return this.adminService.getSite(this.getCurrentUser.sites[0].site_id)
              } else {
                return this.adminService.getSite(this.siteIDs[0])
              }
            }
          } else if (this.selectedPreset.startsWith('year')) {
            let year = this.selectedPreset.split('-')[1]
            start = moment(new Date('01/01/' + year), this.dateFormat)
            end = moment(new Date('12/31/' + year), this.dateFormat)
          } else {
            return
          }

          return { start: start, end: end }
        })
        .then(data => {
          this.$log.debug('Data', data)
          if (data.start) {
            return data
          } else {
            let site = data.result
            this.sitesLaunch[site] = site.launch_date
            return { start: moment(site.launch_date, 'YYYY-MM-DD'), end: end }
          }
        })
        .then(result => {
          this.selectedPeriod = result
          this.$emit('update', result)
        })
        .catch(error => {
          console.error('Error:', error)
        })
    },
    validatePeriod: function() {
      this.$log.debug('validatePeriod')

      if (this.isSmartphoneDevice || this.isSmallScreen) {
        this.emitNewDateRange(this.selectedPeriod.start, this.selectedPeriod.end)
      }

      this.savedStart = moment(this.selectedPeriod.start, this.dateFormat).format(this.dateFormat)
      this.savedEnd = moment(this.selectedPeriod.end, this.dateFormat).format(this.dateFormat)

      this.$log.debug('validatePeriod saved', this.savedStart, this.savedEnd)
      this.show = false
    },
    initPreset: function() {
      if (
        moment(this.start, this.dateFormat).format(this.dateFormat) ==
          this.moment()
            .startOf('isoWeek')
            .add('-2', 'd')
            .format(this.dateFormat) &&
        moment(this.end, this.dateFormat).format(this.dateFormat) ==
          this.moment()
            .startOf('isoWeek')
            .format(this.dateFormat)
      ) {
        this.selectedPreset = 'last_weekend'
      } else if (
        moment(this.start, this.dateFormat).format(this.dateFormat) ==
          moment(new Date(), this.dateFormat)
            .add(-7, 'days')
            .format(this.dateFormat) &&
        moment(this.end, this.dateFormat).format(this.dateFormat) ==
          moment(new Date(), this.dateFormat).format(this.dateFormat)
      ) {
        this.selectedPreset = 'last_week'
      } else if (
        moment(this.start, this.dateFormat).format(this.dateFormat) ==
          moment(new Date(), this.dateFormat)
            .add(-30, 'days')
            .format(this.dateFormat) &&
        moment(this.end, this.dateFormat).format(this.dateFormat) ==
          moment(new Date(), this.dateFormat).format(this.dateFormat)
      ) {
        this.selectedPreset = 'last_month'
      } else if (
        moment(this.start, this.dateFormat).format(this.dateFormat) ==
          moment(new Date(), this.dateFormat)
            .add(-1, 'year')
            .format(this.dateFormat) &&
        moment(this.end, this.dateFormat).format(this.dateFormat) ==
          moment(new Date(), this.dateFormat).format(this.dateFormat)
      ) {
        this.selectedPreset = 'last_year'
      } else {
        this.selectedPreset = 'custom'
      }
    },
    emitNewDateRange: function(start, end) {
      this.$emit('update', {
        start: moment(start).format(this.dateFormat),
        end: moment(end).format(this.dateFormat),
      })
    },
    updateDateRangeBrush: function(dateRange) {
      this.timelineDatePickerStart = moment(dateRange.start).format(this.dateFormat)
      this.timelineDatePickerEnd = moment(dateRange.end).format(this.dateFormat)
    },
    onValidDateRangeBrush: function(dateRange) {
      this.updateDateRangeBrush(dateRange)
      this.$emit('update', dateRange)
    },
  },
}
</script>
